<template>
  <div class="w-full flex flex-col flex-grow items-center p-1 relative min-h-0">
<!-- Loading and errors -->
    <WorkingAndError :isWorking="isWorking" :error="responseError" @clearError="responseError = null" />
<!-- Success message -->
    <transition name="fade">
      <div v-if="successMessage" class="w-full max-w-5xl whitespace-normal truncate chip-green p-2 my-1 text-center cursor-pointer" @click="successMessage = null">
        {{ successMessage }}
      </div>
    </transition>
<!-- Project number mismatch (estimate import) -->
    <div
      v-if="showProjectMismatchOverlay"
      class="w-screen h-screen fixed inset-0 bg-neutral-500 dark:bg-neutral-900 bg-opacity-80 dark:bg-opacity-90 flex items-center justify-center z-[9999] p-2"
      role="dialog"
      aria-modal="true"
      aria-labelledby="co-import-mismatch-title"
    >
      <div class="w-full max-w-xl panel rounded-lg p-6 sm:p-8 flex flex-col space-y-1 shadow-xl mx-4 min-w-[320px]"
        @click.stop
      >
        <div id="co-import-mismatch-title" class="text-lg font-semibold text-center break-words min-w-0 w-full text-sky">
          Project # Mismatch
        </div>
        <div class="w-full space-y-2 leading-relaxed text-left break-words min-w-0 sm:text-sm">
          <p class="whitespace-normal">
            This spreadsheet’s company project number does not match the open project:
          </p>
          <ul class="w-full list-disc pl-5 px-0">
            <li><span class="font-medium">Spreadsheet:</span> {{ mismatchSheetCpn }}</li>
            <li><span class="font-medium">Open project:</span> {{ mismatchProjectCpn }}</li>
          </ul>
          <p class="whitespace-normal opacity-90">
            Continue only if this file is correct for this job or choose No to remove the file.
          </p>
        </div>
        <div class="flex flex-wrap justify-end gap-2 w-full pt-3">
          <button type="button" class="bn-solid-red mr-1" @click="onProjectMismatchNo">No</button>
          <button type="button" class="bn-solid-green" @click="onProjectMismatchYes">
            Yes, proceed
          </button>
        </div>
      </div>
    </div>
<!-- Credit review (pending allocation) -->
    <div v-if="showCreateReviewOverlay" class="flex flex-col flex-grow min-h-0 overflow-y-auto w-full p-1 self-stretch items-center">
      <ChangeOrderCreateReviewPanel
        :credit-lines="creditLines"
        :master-services="coProject?.services ?? []"
        :legacy-change-orders="legacyChangeOrders"
        @back="onCreateReviewBack"
        @confirm="onCreditReviewConfirm"
      />
    </div>
<!-- Main flow — spreadsheet, CO header, services -->
    <div
      v-show="!showCreateReviewOverlay"
      class="w-full flex flex-col flex-grow overflow-y-auto max-w-5xl p-1 min-h-0"
    >
      <div class="w-full p-1 mx-auto">
        <DocumentDropUpload
          ref="estimateDropRef"
          title="Estimate Spreadsheet"
          :allowed-types="changeOrderSpreadsheetMimeTypes"
          :allowed-extensions="changeOrderSpreadsheetExtensions"
          :disabled="isWorking || !coProject"
          :maxFileSize="15 * 1024 * 1024"
          :multiple="false"
          :maxFiles="1"
          @file-selected="onEstimateSpreadsheetSelected"
          @file-removed="resetChangeOrderFormToInitial"
        />
      </div>
      <div v-if="importError" class="w-full mx-auto p-1 text-center whitespace-normal chip-red cursor-pointer hover:opacity-80 my-1" @click="importError = null">
        {{ importError }}
      </div>
      <div class="w-full flex items-center truncate p-1 border-b justify-between flex-wrap border-t border-indigo-sky">
        <div class="flex flex-row items-center truncate flex-wrap">
          <div class="p-1">Change Order #:</div>
          <div class="p-1">
            <input
              v-model="changeOrderNumber"
              type="number"
              class="w-14 text-right"
              min="1"
              max="1000"
              :class="{ 'border border-red-600': !changeOrderNumber }"
              @input="changeOrderNumber = formatInputNumber($event.target.value, 1, 1000)"
              :disabled="isWorking || !coProject || changeOrderNumberFromSheet"
            />
          </div>
        </div>
        <div class="p-1">
          <button
            class="bn-solid-green"
            :disabled="createDisabled || isWorking || !coProject"
            @click="handleCreate()"
          >
            <div>Create</div>
          </button>
        </div>
      </div>
      <div
        class="w-full flex flex-row p-1 border-b truncate text-sm font-semibold"
        :class="{ 'text-red-600': !changeOrderNumber }"
      >
        <div class="flex grow flex-row items-center truncate flex-wrap px-1">
          <div class="p-1">Name:</div>
          <div class="p-1 text-left whitespace-normal truncate">
            {{ newName }}
          </div>
        </div>
        <div class="flex flex-row items-center truncate flex-wrap px-1">
          <div class="p-1">Change Order #:</div>
          <div class="p-1 text-left whitespace-normal truncate">
            {{ newNumber }}
          </div>
        </div>
      </div>
      <div class="w-full text-left whitespace-normal truncate p-1 border-b font-semibold text-sm text-sky">
        Services
      </div>
      <div class="w-full flex items-center justify-center p-1">
        <div class="w-full max-w-lg">
          <searchApi
            :apiEndpoint="'search/partNumbers'"
            :placeholder="'add part # or description'"
            :displayResult="false"
            :disabled="isWorking || !coProject"
            @resultSelected="addServices($event)"
          />
        </div>
      </div>
      <div class="w-full flex flex-auto flex-col h-0 overflow-y-auto p-1">
        <NewServices
          :services="services"
          highlight-invalid-lines
          :is-service-line-valid="serviceLineValid"
          @deleteItem="deleteServiceItem($event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, ref, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { formatInputNumber, newUuid } from '@/shared'
import api from '@/api'
import searchApi from '@/components/ui/searchApi.vue'
import NewServices from '@/components/projects/services/NewServices.vue'
import DocumentDropUpload from '@/components/ui/DocumentDropUpload.vue'
import ChangeOrderCreateReviewPanel from '@/components/projects/changeOrder/ChangeOrderCreateReviewPanel.vue'
import {
  parseChangeOrderEstimateWorkbook,
  sheetCompanyProjectNumberRelatesToParent
} from '@/shared/changeOrderEstimateWorkbook'
import {
  applySingleCreditTargetAutoAllocate,
  isNegativeCreditLine,
  parseServiceHours,
  parseServiceRevenue
} from '@/shared/changeOrderCreditTargets'

// Constants — estimate spreadsheet upload
  const changeOrderSpreadsheetMimeTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.ms-excel.sheet.macroEnabled.12'
  ]

  const changeOrderSpreadsheetExtensions = ['.xlsx', '.xls', '.xlsm']

function serviceLineValid(s) {
  if (typeof s !== 'object' || s === null) return false
  const hours = parseServiceHours(s.hours)
  const revenue = parseServiceRevenue(s.revenue)
  if (!Number.isFinite(hours) || !Number.isFinite(revenue)) return false
  if (hours > 0 && revenue < 0) return false
  if (hours < 0 && revenue >= 0) return false
  return true
}

export default {
  name: 'ChangeOrder',
  components: { searchApi, NewServices, DocumentDropUpload, ChangeOrderCreateReviewPanel },
  props: {
    // ... route / parent — master project id (loads GET …/changeOrder/context/:id)
    projectId: { type: [String, Object], default: null }
  },
  emits: ['newSave'],
  setup(props) {
    // Refs — inject, working state, master project, draft services, import & mismatch, credit review, legacy
      const global = inject('global')
      const { setModalBlocked, sendChangeEvent } = global
      const isWorking = ref(false)
      const responseError = ref(null)
      const successMessage = ref(null)
      const coProject = ref(null)
      const changeOrderNumber = ref(1)
      const services = ref([])
      const importError = ref(null)
      const changeOrderNumberFromSheet = ref(false)
      const estimateDropRef = ref(null)
      const showProjectMismatchOverlay = ref(false)
      const mismatchSheetCpn = ref('')
      const mismatchProjectCpn = ref('')
      const pendingImportAfterMismatch = ref(null)
      const showCreateReviewOverlay = ref(false)
      const legacyChangeOrders = ref([])

    // Computed — resolved id from prop
      const resolvedProjectId = computed(() => {
        const id = props.projectId
        if (id == null || id === '') return null
        return String(id)
      })

    // Computed — credits and row validation
      const negativeLineCount = computed(() => {
        const list = services.value
        if (!Array.isArray(list)) return 0
        let n = 0
        for (let i = 0; i < list.length; i++) {
          if (isNegativeCreditLine(list[i])) n += 1
        }
        return n
      })

      const hasNegativeServiceLines = computed(() => negativeLineCount.value > 0)

      const creditLines = computed(() => services.value.filter(isNegativeCreditLine))

      const servicesValid = computed(() => {
        const list = services.value
        if (!Array.isArray(list)) return false
        return list.every(serviceLineValid)
      })

      const createDisabled = computed(
        () =>
          !coProject.value ||
          !changeOrderNumber.value ||
          !services.value.length ||
          !servicesValid.value
      )

    // Computed — CO label / number display
      const formattedChangeOrderKey = computed(() => {
        const n = changeOrderNumber.value
        if (n == null || n === '') return null
        const num = Number(n)
        if (!Number.isFinite(num)) return null
        return num < 10 ? `0${num}` : String(num)
      })

      const newName = computed(() => {
        if (!coProject.value) return ''
        if (changeOrderNumber.value) {
          const key = formattedChangeOrderKey.value ?? changeOrderNumber.value
          const name = coProject.value?.name ?? ''
          const suffix = ` - ${key}`
          const truncatedName = name.slice(0, Math.max(0, 100 - suffix.length))
          return `${truncatedName}${suffix}`
        }
        return coProject.value?.name ?? ''
      })

      const newNumber = computed(() => {
        if (!coProject.value) return ''
        if (changeOrderNumber.value) {
          const key = formattedChangeOrderKey.value ?? changeOrderNumber.value
          return `${coProject.value?.companyProjectNumber}-${key}`
        }
        return coProject.value?.companyProjectNumber ?? ''
      })

    // Helpers — file read for workbook import
      const readFileAsArrayBuffer = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = () => reject(reader.error)
          reader.readAsArrayBuffer(file)
        })

    // API — legacy CO projects, master context, and create change order services
      const loadLegacyChangeOrders = async () => {
        const id = resolvedProjectId.value
        if (!id) {
          legacyChangeOrders.value = []
          return
        }
        try {
          const res = await api.get(`projects/changeOrder/legacy/${id}`)
          const data = res.data?.data
          legacyChangeOrders.value = Array.isArray(data)
            ? data.map((p) => ({
                ...p,
                services: Array.isArray(p?.services) ? p.services : []
              }))
            : []
        } catch {
          legacyChangeOrders.value = []
        }
      }

      const loadProjectContext = async ({ silent = false } = {}) => {
        const id = resolvedProjectId.value
        if (!id) {
          coProject.value = null
          return
        }
        if (!silent) {
          isWorking.value = true
          responseError.value = null
        }
        try {
          const res = await api.get(`projects/changeOrder/context/${id}`)
          const data = res.data?.data
          coProject.value =
            data && typeof data === 'object'
              ? {
                  ...data,
                  services: Array.isArray(data.services) ? data.services : []
                }
              : null
          if (coProject.value) {
            void loadLegacyChangeOrders()
          } else {
            legacyChangeOrders.value = []
          }
        } catch (e) {
          if (!silent) {
            coProject.value = null
            legacyChangeOrders.value = []
            responseError.value = e.response?.data?.error || e.message || 'Could not load project for change order.'
          }
        } finally {
          if (!silent) {
            isWorking.value = false
          }
        }
      }

      const runCreateAndReset = async () => {
        const projectId = coProject.value?._id
        isWorking.value = true
        responseError.value = null
        try {
          const res = await api.post(`projects/changeOrder/services/${projectId}`, { changeOrderKey: formattedChangeOrderKey.value, services: buildServicesPayload() })
          const updatedProject = res.data?.data
          const modifiedProjectIds = res.data?.modifiedProjectIds ?? []
          if (updatedProject && typeof updatedProject === 'object') {
            coProject.value = {
              ...updatedProject,
              services: Array.isArray(updatedProject.services) ? updatedProject.services : []
            }
          }
          for (const pid of modifiedProjectIds) {
            sendChangeEvent('project', { projectId: pid, isServiceChange: true })
          }
          resetChangeOrderFormToInitial()
          void nextTick(() => {
            estimateDropRef.value?.clearAll?.()
          })
          successMessage.value = 'Change order created successfully.'
          setTimeout(() => { successMessage.value = null }, 2500)
        } catch (e) {
          responseError.value = e.response?.data?.error || e.message || 'Could not create change order.'
        } finally {
          isWorking.value = false
        }
      }

    // Handlers — cross-tab project / services refresh
      const handleSocketChange = (e) => {
        if (!e.detail?.type) return
        switch (e.detail.type) {
          case 'project': {
            const d = e.detail.data
            const cur = resolvedProjectId.value
            const sameProject = cur && d?.projectId && String(d.projectId) === String(cur)
            const detailsOrServices =
              d?.detailChange === true || d?.isServiceChange === true
            if (sameProject && detailsOrServices) void loadProjectContext({ silent: true })
            break
          }
          default:
            break
        }
      }

    // Helpers — reset form and apply parsed workbook rows (before watch — watch calls reset)
      const CO_NUMBER_IMPORT_HINT =
        'Could not determine change order number from the spreadsheet. Expected a company project number with a CO suffix (e.g. this project plus -02).'

      const resetChangeOrderFormToInitial = () => {
        importError.value = null
        responseError.value = null
        changeOrderNumberFromSheet.value = false
        changeOrderNumber.value = 1
        services.value = []
        showProjectMismatchOverlay.value = false
        pendingImportAfterMismatch.value = null
        showCreateReviewOverlay.value = false
        legacyChangeOrders.value = []
      }

      const applyParsedRowsToServices = (parsed) => {
        services.value = parsed
          .map((s) => ({
            ...s,
            localId: newUuid(),
            document: 'contract'
          }))
          .sort((a, b) => {
            const orderA = a.displayOrder !== undefined ? a.displayOrder : 1
            const orderB = b.displayOrder !== undefined ? b.displayOrder : 1
            return orderA - orderB
          })
      }

    // Watchers — master project id from parent
      watch(
        resolvedProjectId,
        (id, prev) => {
          if (!id) {
            coProject.value = null
            responseError.value = null
            resetChangeOrderFormToInitial()
            return
          }
          if (id !== prev) {
            resetChangeOrderFormToInitial()
          }
          void loadProjectContext({ silent: false })
        },
        { immediate: true }
      )

    // Lifecycle — socket listener
      onMounted(() => {
        window.addEventListener('data_change_from_socket', handleSocketChange)
      })

      onUnmounted(() => {
        window.removeEventListener('data_change_from_socket', handleSocketChange)
      })

    // Handlers — service draft rows
      const addServices = function (e) {
        const localId = newUuid()
        if (typeof e === 'object' && !Array.isArray(e)) {
          const objectToAdd = {
            localId,
            document: 'contract',
            displayOrder: e.categoryId?.displayOrder || 1,
            description: e.description,
            partNumber: e.partNumber,
            hours: 0,
            revenue: 0
          }
          services.value.push(objectToAdd)
          services.value.sort((a, b) => {
            const orderA = a.displayOrder !== undefined ? a.displayOrder : 1
            const orderB = b.displayOrder !== undefined ? b.displayOrder : 1
            return orderA - orderB
          })
        }
      }

      const deleteServiceItem = (e) => {
        const index = services.value.findIndex((s) => s.localId === e)
        if (index > -1) {
          services.value.splice(index, 1)
        }
      }

    // Handlers — CPN mismatch modal
      const onProjectMismatchNo = () => {
        showProjectMismatchOverlay.value = false
        pendingImportAfterMismatch.value = null
        void nextTick(() => {
          estimateDropRef.value?.clearAll?.()
        })
      }

      const onProjectMismatchYes = () => {
        const pending = pendingImportAfterMismatch.value
        showProjectMismatchOverlay.value = false
        pendingImportAfterMismatch.value = null
        if (!pending) return
        const { parsed, coFromSheet } = pending
        if (coFromSheet != null) {
          changeOrderNumber.value = formatInputNumber(String(coFromSheet), 1, 1000)
          changeOrderNumberFromSheet.value = true
          importError.value = null
        } else {
          changeOrderNumberFromSheet.value = false
          importError.value = CO_NUMBER_IMPORT_HINT
        }
        applyParsedRowsToServices(parsed)
      }

    // Handlers — estimate spreadsheet import
      const onEstimateSpreadsheetSelected = async (file) => {
        responseError.value = null
        importError.value = null
        changeOrderNumberFromSheet.value = false
        showProjectMismatchOverlay.value = false
        pendingImportAfterMismatch.value = null
        isWorking.value = true
        setModalBlocked(true)
        try {
          const buffer = await readFileAsArrayBuffer(file)
          const {
            services: parsed,
            changeOrderNumber: coFromSheet,
            sheetCompanyProjectNumber,
            errors
          } = await parseChangeOrderEstimateWorkbook(buffer, {
            parentCompanyProjectNumber: coProject.value?.companyProjectNumber ?? ''
          })
          if (errors.length > 0) {
            responseError.value =
              errors.slice(0, 12).join('\n') +
              (errors.length > 12 ? `\n… and ${errors.length - 12} more` : '')
            return
          }
          const parentCpn = coProject.value?.companyProjectNumber ?? ''
          if (!sheetCompanyProjectNumberRelatesToParent(sheetCompanyProjectNumber, parentCpn)) {
            mismatchSheetCpn.value = (sheetCompanyProjectNumber || '').trim() || '(empty)'
            mismatchProjectCpn.value = (parentCpn || '').trim() || '(none)'
            pendingImportAfterMismatch.value = {
              parsed,
              coFromSheet
            }
            showProjectMismatchOverlay.value = true
            return
          }
          if (coFromSheet != null) {
            changeOrderNumber.value = formatInputNumber(String(coFromSheet), 1, 1000)
            changeOrderNumberFromSheet.value = true
            importError.value = null
          } else {
            changeOrderNumberFromSheet.value = false
            importError.value = CO_NUMBER_IMPORT_HINT
          }
          applyParsedRowsToServices(parsed)
        } catch (e) {
          responseError.value =
            e?.message || 'Could not read spreadsheet. Use .xlsx or .xlsm (same template as project import).'
        } finally {
          isWorking.value = false
          setModalBlocked(false)
        }
      }

    // Helpers — build services payload
      const buildServicesPayload = () => {
        const key = formattedChangeOrderKey.value
        return services.value.map((s) => {
          const rest = { ...s }
          delete rest.localId
          delete rest.creditAllocationAuto
          const target = rest.creditApplyTarget
          delete rest.creditApplyTarget
          if (isNegativeCreditLine(rest) && target) {
            const targetDoc = target.document
            rest.document = targetDoc
            rest.originatingDocument = key && key !== targetDoc ? key : null
            rest.creditTargetProjectId = target.scope === 'legacy' ? target.projectId : null
          } else {
            rest.document = key || rest.document
            rest.originatingDocument = null
          }
          return rest
        })
      }

    // Handlers — create and credit review dismiss
      const handleCreate = async () => {
        responseError.value = null
        importError.value = null
        if (hasNegativeServiceLines.value) {
          await loadLegacyChangeOrders()
          applySingleCreditTargetAutoAllocate(services.value, coProject.value?.services ?? [], legacyChangeOrders.value)
          const allAutoAllocated = creditLines.value.every(s => s.creditAllocationAuto)
          if (allAutoAllocated) {
            await runCreateAndReset()
            return
          }
          showCreateReviewOverlay.value = true
          setModalBlocked(true)
          return
        }
        await runCreateAndReset()
      }

      const onCreateReviewBack = () => {
        for (const s of services.value) {
          delete s.creditApplyTarget
          delete s.creditAllocationAuto
        }
        showCreateReviewOverlay.value = false
        setModalBlocked(false)
      }

      const onCreditReviewConfirm = () => {
        setModalBlocked(false)
        runCreateAndReset()
      }

    return {
      // Loading & errors
      isWorking,
      responseError,
      successMessage,
      // Mismatch modal
      showProjectMismatchOverlay,
      mismatchSheetCpn,
      mismatchProjectCpn,
      onProjectMismatchNo,
      onProjectMismatchYes,
      // Credit review overlay
      showCreateReviewOverlay,
      creditLines,
      legacyChangeOrders,
      onCreateReviewBack,
      onCreditReviewConfirm,
      // Master & form fields
      coProject,
      changeOrderNumber,
      changeOrderNumberFromSheet,
      importError,
      newName,
      newNumber,
      createDisabled,
      // Spreadsheet upload
      estimateDropRef,
      changeOrderSpreadsheetMimeTypes,
      changeOrderSpreadsheetExtensions,
      onEstimateSpreadsheetSelected,
      resetChangeOrderFormToInitial,
      // Services list
      services,
      addServices,
      deleteServiceItem,
      serviceLineValid,
      // Create
      handleCreate,
      // Shared (template)
      formatInputNumber
    }
  }
}
</script>
